import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Redirect } from "react-router-dom";

import {
  Button,
  CardStack,
  Dropdown,
  FilterPanel,
  Lightbox,
  Slider,
  Toggle,
  VimeoContainer,
} from "../components";
import {
  accessors,
  CHART_DIMENSIONS,
  SOUNDMACHINE_VIDEO_URL,
  urls,
  isSmallViewport,
} from "../utils";
import platformStories from "../data/platform_stories";
import { useStore } from "../store";
import { NoDataModal, StoryModal } from "./platform-components/Modals";
import { makeNarrative } from "../data/narrative";
import "./Platform.scss";

export default function Platform() {
  const actions = useStore(useCallback((state) => state.actions, []));
  const filteredData = useStore(
    useCallback((state) => state.app.timeline.attributes, [])
  );
  const [showTutorial, setShowTutorial] = useState(false);
  const data = useStore(useCallback((state) => state.data.events, []));
  const dispatch = useStore(useCallback((state) => state.dispatch, []));
  const hoveredData = useStore(useCallback((state) => state.app.selected, []));
  const currentStory = useStore(
    useCallback((state) => state.platform.story.selected, [])
  );
  const showLightbox = useStore(
    useCallback((state) => state.app.lightbox.show, [])
  );

  const categories = useStore(
    useCallback((state) => state.data.categories, [])
  );
  const isCarpetView = useStore(
    useCallback((state) => state.app.timeline.topView, [])
  );
  const isOrthographicCamera = useStore(
    useCallback((state) => state.app.timeline.orthographic, [])
  );

  const timelineScale = useStore(
    useCallback((state) => state.app.timeline.scales.width, [])
  );

  const narrativeData = useMemo(() => makeNarrative({ data }), [data]);

  const onTimelineViewToggle = useCallback(
    (val) => {
      dispatch({
        type: `TIMELINE_UPDATE_SETTINGS`,
        payload: {
          topView: val === `Countries`,
        },
      });
    },
    [dispatch]
  );

  const onLighboxToggle = useCallback(() => {
    dispatch({
      type: `TOGGLE_LIGHTBOX`,
      payload: {
        show: !showLightbox,
      },
    });
  }, [dispatch, showLightbox]);

  const onOrthographicModeToggle = useCallback(
    (val) => {
      dispatch({
        type: `TIMELINE_UPDATE_SETTINGS`,
        payload: {
          orthographic: val === `2D`,
          topView: false,
          scales: {
            showLocalTime: false, // TODO Do we use this?
            width: CHART_DIMENSIONS.X.MAX,
          },
        },
      });
    },
    [dispatch]
  );

  const onPlatformStorySelect = useCallback(
    (selectedLabel) => {
      actions.selectPlatformStory(
        platformStories.find((d) => d.label === selectedLabel)
      );
    },
    [actions]
  );

  const onCardHover = useCallback((e) => {
    // console.log(`hovered over event`, e.target.dataset.id, "in card");
  }, []);

  const showCardStack =
    hoveredData?.length > 0 || filteredData.length !== data.length;

  const goToTutorial = () => {
    setShowTutorial(true);
  };

  useEffect(() => {
    actions.hideOnboarding();
    actions.changeOnboardingIdx(narrativeData.length - 1);

    return function () {
      // TODO use a better conditional for disabling touch events on canvas
      // actions.showOnboarding();
      // actions.changeOnboardingIdx(0);
      // Reset the showOnboarding flag, as we are currently using this to disable touch events on the canvas
      // TODO Find a way to reset the visualization if the camera is orthographic
      actions.exitPlatform();
    };
  }, [actions, narrativeData]);

  return (
    <div className="platform px2 fade-in" style={{ display: `initial` }}>
      {showTutorial && <Redirect to={urls.explore.path} />}
      <FilterPanel
        collapsed={isSmallViewport()}
        className="platform__filter-panel absolute left-0 z2 fade-in fixed top-navbar px2 border-right full-height-minus-navbar"
      />
      <div className="platform__filter-panel-extension">
        <Button className="platform__show-intro" onClick={goToTutorial}>
          <div className="block-font h4">Show Intro</div>
        </Button>
        <Button
          className="platform__listen-to-the-data"
          onClick={onLighboxToggle}
        >
          <div className="block-font h4">Listen to the Data</div>
        </Button>
      </div>

      <div className="platform__timeline-controls-container h4">
        <div className="platform__timeline-controls flex flex-row items-end items-center">
          <Dropdown
            className="platform__story-dropdown block-font bg-primary pointer-events z2 mr3"
            value={currentStory?.label || null}
            defaultValue={platformStories[0].label}
            options={platformStories.map((c) => c.label)}
            onChange={onPlatformStorySelect}
          />
          <Toggle
            className="xs-hide sm-hide pointer-events rounded-lg bg-primary"
            title={""}
            selected={
              !isOrthographicCamera
                ? isCarpetView
                  ? "Countries"
                  : "Targets"
                : "Targets"
            }
            values={
              isOrthographicCamera ? ["Targets"] : ["Targets", "Countries"]
            }
            onChange={onTimelineViewToggle}
          />
          <Toggle
            className="xs-hide sm-hide pointer-events rounded-lg ml1 bg-primary"
            title={""}
            selected={isOrthographicCamera ? "2D" : "3D"}
            values={["3D", "2D"]}
            onChange={onOrthographicModeToggle}
          />
        </div>
        {isOrthographicCamera && (
          <div className="fade-in flex flex-row">
            <Slider
              className="mt2"
              selected={timelineScale}
              min={CHART_DIMENSIONS.X.MAX}
              max={500}
              step="50"
              width={250}
              valueWidth={`12ch`}
              title={"Zoom level"}
              descriptor={""}
              amountFormatter={() => ``}
              hideAmountLabel
              onChange={(val) => {
                actions.updateTimelineWidth(+val);
              }}
            />
          </div>
        )}
      </div>
      <StoryModal
        className="flex flex-row justify-around block-font h2"
        onClose={actions.hideStoryNotes}
      />
      {filteredData.length === 0 && (
        <NoDataModal
          className="no-data-modal z2 absolute m-auto flex flex-row justify-around block-font h2"
          style={{
            left: `calc(var(--panel-width) - var(--panel-x-offset))`,
            width: `calc(100vw - var(--panel-width) - var(--panel-x-offset))`,
            top: `45%`,
          }}
        />
      )}
      {showLightbox && (
        <Lightbox onClose={onLighboxToggle}>
          <div className="absolute min-full-height self-center col-9 xs-col-12">
            <VimeoContainer
              className="mb2 col-12 min-full-height"
              url={SOUNDMACHINE_VIDEO_URL}
            />
          </div>
        </Lightbox>
      )}
      <CardStack
        showTableHeaders={false}
        showHistogram={false}
        show={showCardStack}
        sortFunc={(a, b) =>
          accessors.date.start(a).getTime() - accessors.date.start(b).getTime()
        }
        onCardHover={onCardHover}
        collapsed={isSmallViewport() ? true : false}
        data={
          hoveredData?.length > 0
            ? hoveredData
            : showCardStack
            ? filteredData
            : []
        }
        // this is needed so we don't catch the flicker of whatever data
        // we provide to the card stack before it animates out
        // (with the "show" prop)
        style={{
          display: showCardStack ? `flex` : `none`,
        }}
        className="z2 fixed bottom-0"
        associationColor={(d) =>
          categories.find((c) => c.name === accessors.event.type(d))?.rgba ||
          `transparent`
        }
      />
    </div>
  );
}
